<template>
  <div class="quick-course-creator">
    <div class="quick-course-header">
      <h2>Quick Course Creator</h2>
      <p>Create a new course with minimal required fields</p>
    </div>

    <div class="form-container">
      <div v-if="successMessage" class="success-message">
        {{ successMessage }}
      </div>
      <div v-if="errorMessage" class="error-message">
        {{ errorMessage }}
      </div>

      <form @submit.prevent="submitCourse">
        <!-- Basic Information -->
        <div class="form-section">
          <div class="form-group">
            <label for="title">Course Title *</label>
            <input
              id="title"
              v-model="formData.title"
              type="text"
              class="form-control"
              required
              placeholder="Enter course title"
            />
          </div>
          
          <div class="form-group">
            <label for="title_ar">Course Title (Arabic) *</label>
            <input
              id="title_ar"
              v-model="formData.title_ar"
              type="text"
              class="form-control"
              dir="rtl"
              lang="ar"
              required
              placeholder="أدخل عنوان الدورة"
            />
          </div>

          <div class="form-group">
            <label for="description">Course Description *</label>
            <QuillEditor
              id="description"
              v-model:content="formData.description"
              label="Description"
              :toolbar="toolbarOptions"
              required
              placeholder="Enter course description"
            />
          </div>

          <div class="form-group">
            <label for="description_ar">Course Description (Arabic) *</label>
            <QuillEditor
              id="description_ar"
              v-model:content="formData.description_ar"
              label="Description (Arabic)"
              :toolbar="toolbarOptions"
              required
              placeholder="أدخل وصف الدورة"
            />
          </div>

          <div class="form-group">
            <label for="instructor_id">Instructor *</label>
            <select
              id="instructor_id"
              v-model="formData.instructor_id"
              class="form-control"
              required
              :disabled="isLoading"
            >
              <option value="">{{ isLoading ? 'Loading instructors...' : 'Select an instructor' }}</option>
              <option v-for="instructor in instructors" :key="instructor.id" :value="instructor.user_id">
                {{ instructor.first_name + ' ' + instructor.last_name }}
              </option>
            </select>
          </div>

          <div class="form-group">
            <label for="category_id">Category *</label>
            <select
              id="category_id"
              v-model="formData.category_id"
              class="form-control"
              required
            >
              <option value="">Select a category</option>
              <option v-for="category in categories" :key="category.id" :value="category.id">
                {{ category?.title || 'Category ' + category.id }}
              </option>
            </select>
          </div>
        </div>

        <!-- Quick Settings -->
        <div class="form-section">
          <div class="form-group">
            <label for="price">Price *</label>
            <input
              id="price"
              v-model.number="formData.price"
              type="number"
              step="0.01"
              class="form-control"
              required
              placeholder="Enter course price"
            />
          </div>

          <div class="form-group">
            <label for="minutes">Duration (minutes) *</label>
            <input
              id="minutes"
              v-model.number="formData.minutes"
              type="number"
              min="1"
              class="form-control"
              required
              placeholder="Enter course duration in minutes"
            />
          </div>

          <div class="form-group">
            <label for="status">Status *</label>
            <select
              id="status"
              v-model="formData.status"
              class="form-control"
              required
            >
              <option value="active">Active</option>
              <option value="draft">Draft</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div class="form-group checkbox-group">
            <label class="checkbox-label">
              <input type="checkbox" v-model="formData.is_premium" />
              Premium Course
            </label>
            
            <label class="checkbox-label">
              <input type="checkbox" v-model="formData.top_course" />
              Top Course
            </label>
            
            <label class="checkbox-label">
              <input type="checkbox" v-model="formData.is_active" />
              Active Course
            </label>
          </div>
        </div>

        <!-- Cover Images -->
        <div class="form-section">
          <div class="form-group">
            <label for="audio_cover">Cover Image *</label>
            <div class="upload-container">
              <div class="upload-button" @click="openUploadWidget('audio_cover')">
                <span>Upload Cover Image</span>
              </div>
              <div v-if="formData.audio_cover" class="upload-preview">
                <img :src="formData.audio_cover" alt="Cover preview" class="preview-image" />
                <span class="file-name">{{ getFileName(formData.audio_cover) }}</span>
              </div>
            </div>
          </div>

          <div class="form-group">
            <label for="audio_cover_ar">Cover Image (Arabic) *</label>
            <div class="upload-container">
              <div class="upload-button" @click="openUploadWidget('audio_cover_ar')">
                <span>Upload Cover Image (Arabic)</span>
              </div>
              <div v-if="formData.audio_cover_ar" class="upload-preview">
                <img :src="formData.audio_cover_ar" alt="Arabic cover preview" class="preview-image" />
                <span class="file-name">{{ getFileName(formData.audio_cover_ar) }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="form-actions">
          <button type="submit" class="submit-button" :disabled="isSubmitting">
            {{ isSubmitting ? 'Creating Course...' : 'Create Course' }}
          </button>
          <button type="button" class="cancel-button" @click="resetForm">
            Reset Form
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import cloudflareR2Service from '@/services/cloudflareR2'
import QuillEditor from '@/components/QuillEditor.vue'

export default {
  name: 'QuickCourseCreator',
  components: {
    QuillEditor
  },
  setup() {
    const router = useRouter()
    const instructors = ref([])
    const categories = ref([])
    const isLoading = ref(true)
    const isSubmitting = ref(false)
    const successMessage = ref('')
    const errorMessage = ref('')
    const uploadError = ref('')
    const toolbarOptions = ref([
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote'],
      [{ 'header': 1 }, { 'header': 2 }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'indent': '-1' }, { 'indent': '+1' }],
      [{ 'direction': 'rtl' }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['clean'],
      ['link']
    ])

    // Initialize form data with default values
    const formData = reactive({
      title: '',
      title_ar: '',
      description: '',
      description_ar: '',
      instructor_id: '',
      category_id: '',
      price: 0,
      minutes: 30,
      status: 'draft',
      is_active: true,
      is_premium: false,
      top_course: false,
      audio_cover: '',
      audio_cover_ar: '',
      audio_url: '', // Will be populated later in the full editor
      audio_url_ar: '', // Will be populated later in the full editor
    })

    // Fetch instructors from API
    const fetchInstructors = async () => {
      try {
        const token = localStorage.getItem('token') || '5126|IEDUjZGv6eA7s8xTHlxqhZzAoR7SEhaamY5CC9qx0bcdaaf2'
        
        const response = await fetch('https://api.kewaar.com/api/admin/instructors', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        })

        const data = await response.json()
        
        if (response.ok && data.success) {
          instructors.value = data.data
        } else {
          console.error('Failed to fetch instructors:', data.message)
          errorMessage.value = 'Failed to load instructors. Please refresh the page.'
        }
        isLoading.value = false
      } catch (error) {
        console.error('Error fetching instructors:', error)
        errorMessage.value = 'An error occurred while loading instructors. Please refresh the page.'
        isLoading.value = false
      }
    }

    // Fetch categories from API
    const fetchCategories = async () => {
      try {
        const token = localStorage.getItem('token') || '5126|IEDUjZGv6eA7s8xTHlxqhZzAoR7SEhaamY5CC9qx0bcdaaf2'
        
        const response = await fetch('https://api.kewaar.com/api/admin/coursecategories2', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        })

        const data = await response.json()
        
        if (response.ok && data.success) {
          categories.value = data.data.filter(category => category.is_active)
        } else {
          console.error('Failed to fetch categories:', data.message)
          errorMessage.value = 'Failed to load categories. Please refresh the page.'
        }
      } catch (error) {
        console.error('Error fetching categories:', error)
        errorMessage.value = 'An error occurred while loading categories. Please refresh the page.'
      }
    }

    // Initialize component
    onMounted(() => {
      fetchInstructors()
      fetchCategories()
    })

    // Submit form
    const submitCourse = async () => {
      if (isSubmitting.value) return
      
      isSubmitting.value = true
      successMessage.value = ''
      errorMessage.value = ''
      
      try {
        const token = localStorage.getItem('token') || '5126|IEDUjZGv6eA7s8xTHlxqhZzAoR7SEhaamY5CC9qx0bcdaaf2'
        
        // For the quick creation, we'll set empty audio URLs that can be updated later
        if (!formData.audio_url) {
          formData.audio_url = formData.audio_cover // Temporarily use cover as placeholder
        }
        
        if (!formData.audio_url_ar) {
          formData.audio_url_ar = formData.audio_cover_ar // Temporarily use cover as placeholder
        }
        
        const response = await fetch('https://api.kewaar.com/api/admin/courses', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(formData)
        })

        const data = await response.json()
        
        if (response.ok) {
          successMessage.value = 'Course created successfully!'
          // Redirect to courses page after a short delay
          setTimeout(() => {
            router.push('/courses')
          }, 1500) // 1.5 second delay to show the success message
        } else {
          errorMessage.value = data.message || 'Failed to create course'
          if (data.errors) {
            errorMessage.value = Object.entries(data.errors)
              .map(([field, errors]) => `${field}: ${errors.join(', ')}`)
              .join('\n')
          }
        }
      } catch (error) {
        console.error('Error creating course:', error)
        errorMessage.value = 'An error occurred while creating the course. Please try again.'
      } finally {
        isSubmitting.value = false
      }
    }

    // Reset form
    const resetForm = () => {
      // Reset all form fields to default values
      Object.assign(formData, {
        title: '',
        title_ar: '',
        description: '',
        description_ar: '',
        instructor_id: '',
        category_id: '',
        price: 0,
        minutes: 30,
        status: 'draft',
        is_active: true,
        is_premium: false,
        top_course: false,
        audio_cover: '',
        audio_cover_ar: '',
        audio_url: '',
        audio_url_ar: '',
      })
      
      successMessage.value = ''
      errorMessage.value = ''
    }

    // Open upload widget for file uploads
    const openUploadWidget = async (field) => {
      uploadError.value = ""
      
      // Create a file input element programmatically
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = 'image/jpeg,image/png,image/gif'
      
      input.onchange = async (event) => {
        const file = event.target.files[0]
        if (!file) return
        
        try {
          const uploadResponse = await cloudflareR2Service.uploadWithValidation(file)
          // Update the field with the file URL
          formData[field] = uploadResponse.fileUrl
        } catch (error) {
          uploadError.value = `Upload failed: ${error.message || "Unknown error"}`
        }
      }
      
      input.click()
    }

    // Helper to extract file name from URL
    const getFileName = (url) => {
      if (!url) return ''
      const parts = url.split('/')
      return parts[parts.length - 1]
    }

    return {
      formData,
      instructors,
      categories,
      isSubmitting,
      isLoading,
      successMessage,
      errorMessage,
      uploadError,
      submitCourse,
      resetForm,
      openUploadWidget,
      getFileName,
      toolbarOptions
    }
  }
}
</script>

<style scoped>
.quick-course-creator {
  padding: 20px;
  max-width: 800px;
  margin: 0 auto;
}

.quick-course-header {
  margin-bottom: 20px;
  text-align: center;
}

.quick-course-header h2 {
  font-size: 1.75rem;
  margin-bottom: 8px;
  color: #333;
}

.quick-course-header p {
  color: #666;
  font-size: 1rem;
}

.form-container {
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  padding: 24px;
}

.form-section {
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid #eee;
}

.form-section:last-child {
  border-bottom: none;
}

.form-group {
  margin-bottom: 16px;
}

label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
}

.form-control {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.2s;
}

.form-control:focus {
  border-color: #4a90e2;
  outline: none;
}

.checkbox-group {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.checkbox-label input[type="checkbox"] {
  margin-right: 8px;
}

.upload-container {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.upload-button {
  padding: 10px 16px;
  background-color: #f1f5f9;
  border: 1px dashed #cbd5e1;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.upload-button:hover {
  background-color: #e2e8f0;
}

.upload-preview {
  display: flex;
  align-items: center;
  gap: 8px;
}

.preview-image {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 4px;
}

.file-name {
  font-size: 12px;
  color: #64748b;
  word-break: break-all;
  max-width: 200px;
}

.form-actions {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-top: 24px;
}

.submit-button, .cancel-button {
  padding: 12px 24px;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  flex: 1;
  border: none;
}

.submit-button {
  background-color: #4a90e2;
  color: white;
}

.submit-button:hover {
  background-color: #3a7bc8;
}

.submit-button:disabled {
  background-color: #a0c3e8;
  cursor: not-allowed;
}

.cancel-button {
  background-color: #f1f5f9;
  color: #64748b;
  border: 1px solid #e2e8f0;
}

.cancel-button:hover {
  background-color: #e2e8f0;
}

.success-message, .error-message {
  padding: 12px;
  border-radius: 4px;
  margin-bottom: 16px;
}

.success-message {
  background-color: #d1fae5;
  color: #065f46;
  border: 1px solid #a7f3d0;
}

.error-message {
  background-color: #fee2e2;
  color: #b91c1c;
  border: 1px solid #fecaca;
  white-space: pre-line;
}
</style> 